<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex xs12 sm4>
          <h1 class="headline">{{ $t('drivers.title') }}</h1>
        </v-flex>
        <v-flex xs12 sm8>
          <v-layout mt-2 wrap align-center justify-end>
            <export-button
              v-if="$can('driver_export')"
              class="ml-auto mr-3"
              action-name="driver/downloadList"
              large
              :file-name="$t('drivers.title')"
              :query="getSearchParams()"
            />

            <v-btn
              v-if="$can('finance_rating_config')"
              type="submit"
              :to="{ name: 'rating-configurator' }"
              class="mr-2"
              color="blue-grey white--text"
              large
              depressed
            >
              {{ $t('driverRating.configurtor') }}
            </v-btn>

            <v-btn
              v-if="$can('driver_add')"
              type="submit"
              :to="{ name: 'add-driver' }"
              color="warning"
              large
              depressed
            >
              {{ $t('common.btns.addDriver') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>

      <div style="margin-top:20px; margin-bottom:10px; display:flex;">
        <div class="search-wrapper">
          <v-text-field
            class="search"
            ref="search"
            v-model="search"
            :placeholder="$t('driversList.search.placeholder')"
            autocomplete="off"
            solo
          ></v-text-field>
          <v-btn
            type="button"
            class="search-btn warning mb-4"
            depressed
            large
            @click="searchDrivers"
          >
            {{ $t('common.btns.search') }}
          </v-btn>
        </div>

        <v-menu
          ref="menuDate"
          :close-on-content-click="false"
          v-model="menuDate"
          :return-value.sync="dates"
          max-width="280px"
          min-width="280px"
          class="pr-4 mt-2"
          transition="scale-transition"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-on="on"
              v-model="dates"
              prepend-icon="mdi-calendar"
              :label="$t('driversList.tableHeaders.createdAt')"
              readonly
              class="filter"
            >
              <template v-if="dates.length === 2" #append>
                <v-icon
                  @click.stop="
                    () => {
                      dates = [];
                      searchDrivers();
                    }
                  "
                >
                  mdi-close
                </v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="dates"
            color="primary lighten-1"
            width="280"
            range
            :locale="$i18n.locale"
            :selected-items-text="dates.toString()"
            :allowed-dates="allowedDates"
            @input="changeDate"
          ></v-date-picker>
        </v-menu>
        <v-select
          :items="verificationStatuses"
          v-model="verificationStatus"
          :label="$t('common.fields.status')"
          item-text="title"
          item-value="id"
          class="filter"
          @change="setVerificationStatus"
          return-object
        ></v-select>
      </div>
    </v-flex>
    <v-flex xs12>
      <v-data-table
        :items="driversList.items"
        :headers="headers"
        :loading="isLoading"
        :loading-text="$t('common.table.dataIsLoading')"
        :server-items-length="2"
        :mobile-breakpoint="100"
        class="table-custom driversList small mb-4"
        hide-default-header
        hide-default-footer
      >
        <template #header="{ props }">
          <thead class="v-data-table-header">
            <tr>
              <th v-for="(header, key) in props.headers" :key="key">
                {{ header.text }}
                <v-btn
                  v-if="header.sortParam"
                  color="rgba(255, 255, 255, 0.8)"
                  class="sort-btn"
                  icon
                  small
                  :class="
                    sort.orderCol === header.sortParam ? sort.orderType : 'asc'
                  "
                  @click="
                    () => {
                      sort.orderCol = header.sortParam;
                      sort.orderType =
                        sort.orderType === 'asc' ? 'desc' : 'asc';
                    }
                  "
                >
                  <v-icon
                    :color="sort.orderCol === header.sortParam ? 'warning' : ''"
                    size="18"
                  >
                    mdi-arrow-up
                  </v-icon>
                </v-btn>
              </th>
            </tr>
          </thead>
        </template>

        <template slot="item" slot-scope="{ item }">
          <tr :key="item.id" :class="{ blocked: item.isBlocked }">
            <td class="text-xs-left">{{ item.num }}</td>
            <td class="text-xs-left">
              <v-layout align-center justify-start>
                {{ item.id }}
              </v-layout>
            </td>
            <td class="text-xs-left">
              <router-link class="driver-link" :to="`/drivers/${item.id}/info`">
                {{ item.name }}
              </router-link>
            </td>
            <td class="text-xs-left">
              <div>{{ item.phone }}</div>
              <div>{{ item.secondPhone }}</div>
            </td>
            <td class="text-xs-left">{{ item.createdAt }}</td>
            <td class="text-xs-left">
              <button
                class="edit-btn"
                :disabled="!$can('driver_edit')"
                @click="
                  () => {
                    updateDialog.opened = true;
                    updateDialog.onlyComment = false;
                    updateDialog.data = item;
                  }
                "
              >
                {{ item.verificationStatus }}
              </button>
              <div v-if="item.isBlocked">{{ $t('common.labels.blocked') }}</div>
            </td>
            <td>
              <div v-if="item.deposit_type">{{ item.deposit_str }}</div>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('driver_edit')"
                @click="
                  () => {
                    updateDialog.opened = true;
                    updateDialog.onlyComment = true;
                    updateDialog.data = item;
                  }
                "
              >
                {{ item.statusComment || '—' }}
              </button>
            </td>
            <td>
              <b :class="getRatingClass(item.rating)">
                {{ item.rating }}
              </b>
            </td>
            <td>{{ item.qty_days_on_line }}</td>
          </tr>
        </template>
        <template slot="no-data">{{ $t('common.table.noData') }}</template>
      </v-data-table>
      <div v-if="paginationPage" class="text-xs-center">
        <v-pagination
          :value="paginationPage"
          :length="paginationLength"
          @input="choosePaginatorPage($event)"
        ></v-pagination>
      </div>
    </v-flex>

    <v-snackbar v-model="snack.opened" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>

    <update-status-dialog
      v-model="updateDialog.opened"
      :only-comment="updateDialog.onlyComment"
      :data="updateDialog.data"
      @updated="proccessResult"
    />
  </v-layout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ExportButton from '@/components/shared/ExportButton.vue';
import UpdateStatusDialog from '../../components/driver/UpdateStatusDialog.vue';

export default {
  name: 'DriversListPage',
  components: { UpdateStatusDialog, ExportButton },
  data() {
    return {
      search: '',
      menuDate: false,
      dates: [],
      updateDialog: {
        opened: false,
        onlyComment: false,
        data: null,
      },
      snack: {
        opened: false,
        message: '',
        color: '',
      },
      status: {
        id: '',
        title: this.$t('drivers.allDrivers'),
      },
      verificationStatus: {
        id: '',
        title: this.$t('drivers.allDrivers'),
      },
      sort: {
        orderCol: '',
        orderType: '',
      },
      isLoading: false,
      headers: [
        {
          text: this.$t('common.number'),
          value: 'num',
        },
        {
          text: this.$t('driversList.tableHeaders.id'),
          value: 'drivers.id',
          sortParam: 'drivers.id',
        },
        { text: this.$t('driversList.tableHeaders.name') },
        { text: this.$t('driversList.tableHeaders.phone') },
        {
          text: this.$t('driversList.tableHeaders.createdAt'),
        },
        { text: this.$t('driversList.tableHeaders.status') },
        { text: this.$t('common.fields.depositType') },
        { text: this.$t('driversList.tableHeaders.statusComment') },
        {
          text: this.$t('driverRating.title'),
          sortParam: 'rating',
        },
        { text: this.$t('driversList.tableHeaders.qty_days') },
      ],
    };
  },
  watch: {
    /* eslint-disable */
    '$i18n.locale': async function() {
      this.isLoading = true;
      await this.fetchDriversProperties();
      await this.fetchDrivers({
        page: this.paginationPage,
        searchString: this.search,
        verificationStatus: this.verificationStatus.id.toString(),
        orderCol: this.sort.orderCol,
        orderType: this.sort.orderType,
        dates: this.dates,
      });
      this.isLoading = false;
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
    sort: {
      deep: true,
      immediate: true,
      handler() {
        this.searchDrivers();
      },
    },
  },
  computed: {
    ...mapGetters({
      driversList: 'driver/getDriversListData',
      driversVerificationStatuses: 'driver/getDriversVerificationStatuses',
    }),
    verificationStatuses() {
      return [this.status].concat(this.driversVerificationStatuses);
    },
    paginationLength() {
      return this.driversList.lastPage;
    },
    paginationPage() {
      if (
        this.driversList.currentPage === 1 &&
        this.driversList.lastPage === 1
      ) {
        return 0;
      }
      return this.driversList.currentPage;
    },
  },
  async created() {
    this.isLoading = true;
    await this.fetchDriversProperties();
    this.isLoading = false;
  },
  destroyed() {
    this.setDriversListData({});
    this.setDriversVerificationStatuses([]);
    this.setDriversPersonalityDocs([]);
    this.setDriversLocations([]);
    this.clearAlertError();
  },
  methods: {
    ...mapActions({
      fetchDrivers: 'driver/fetchDriversListData',
      fetchDriversProperties: 'driver/fetchDriversProperties',
    }),
    ...mapMutations({
      setDriversListData: 'driver/setDriversListData',
      setDriversVerificationStatuses: 'driver/setDriversVerificationStatuses',
      setDriversPersonalityDocs: 'driver/setDriversPersonalityDocs',
      setDriversLocations: 'driver/setDriversLocations',
      clearAlertError: 'global/clearAlertError',
    }),
    getSearchParams(page = 1) {
      return {
        page,
        searchString: this.search,
        verificationStatus: this.verificationStatus.id.toString(),
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        orderCol: this.sort.orderCol,
        orderType: this.sort.orderType,
        dates: this.dates,
      };
    },
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.dates);
        this.searchDrivers();
      }
    },
    allowedDates(value) {
      const [from, to] = this.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    proccessResult({ success, error }) {
      const snack = {
        opened: true,
        message: this.$t('common.saved'),
        color: 'success',
      };

      if (success) {
        this.searchDrivers();
      } else {
        snack.message = error;
        snack.color = 'error';
      }

      this.snack = snack;
    },
    async searchDrivers() {
      this.isLoading = true;
      await this.fetchDrivers(this.getSearchParams());
      this.isLoading = false;
    },
    async choosePaginatorPage(page) {
      this.isLoading = true;
      await this.fetchDrivers(this.getSearchParams(page));
      this.isLoading = false;
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
    async setVerificationStatus() {
      await this.searchDrivers();
    },
    getRatingClass(num) {
      if (num === 0) return 'blue-grey--text';
      return num >= 0 ? 'green--text' : 'red--text';
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-btn {
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #464646;
  line-height: 1.3;
  text-decoration: none;

  &[disabled] {
    font-weight: normal;
  }

  &:not([disabled]):hover {
    opacity: 0.7;
  }
}

.search-wrapper {
  width: 100%;
  max-width: 380px;
  display: flex;
  align-items: center;

  .search {
    margin-right: -12px;
    margin-bottom: -12px;
    max-width: 300px;
    width: 100%;

    @media (max-width: 400px) {
      margin-right: 0px;
      margin-bottom: -12px;
      max-width: 100%;
    }
  }

  @media (max-width: 400px) {
    flex-direction: column;

    .search {
      width: 100%;
    }
  }
}
.filter {
  max-width: 250px;
  margin-left: 25px;
}
.sort-btn {
  transition: transform 0.3s;

  &.desc {
    transform: rotate(180deg);
  }
}
.blocked {
  background: rgba(233, 30, 30, 0.25);
}
.driver-link:hover {
  text-decoration: none;
}
</style>
